import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  List,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CheckCircleOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getMeituanFinancialHealth, type MeituanFinancialHealth as HealthData } from '../services/financeReportService';

const { Text } = Typography;

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const MeituanFinancialHealth: React.FC = () => {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(false);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMeituanFinancialHealth();
      setData(result);
    } catch {
      message.error('获取美团同步健康状态失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHealth();
  }, [loadHealth]);

  const schemaColumns = useMemo<ColumnsType<HealthData['schema'][number]>>(() => [
    {
      title: '检查对象',
      dataIndex: 'table',
      width: 310,
      render: (value: string) => <Text code>{value}</Text>,
    },
    {
      title: '状态',
      dataIndex: 'ready',
      width: 100,
      render: (ready: boolean) => ready ? <Tag color="success">就绪</Tag> : <Tag color="error">未就绪</Tag>,
    },
    {
      title: '缺少字段',
      dataIndex: 'missingColumns',
      render: (values?: string[]) => values?.length ? values.map((value) => <Tag color="error" key={value}>{value}</Tag>) : '—',
    },
    {
      title: '缺少索引',
      dataIndex: 'missingIndexes',
      render: (values?: string[]) => values?.length ? values.map((value) => <Tag color="warning" key={value}>{value}</Tag>) : '—',
    },
    {
      title: '检查错误',
      dataIndex: 'checkError',
      render: (value?: string) => value || '—',
    },
  ], []);

  const taskCounts = data?.syncTasks.statusCounts || {};
  const failedCount = taskCounts.failed || 0;
  const staleProcessingCount = data?.syncTasks.staleProcessingCount || 0;
  const hasTaskIssue = failedCount > 0 || staleProcessingCount > 0;

  return (
    <div style={{ padding: 24 }}>
      <Card
        loading={loading}
        title={(
          <Space size={8}>
            <SafetyOutlined />
            <span>美团同步健康</span>
          </Space>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void loadHealth()}>
            立即检查
          </Button>
        )}
      >
        <Alert
          showIcon
          type={data?.ready && !hasTaskIssue ? 'success' : 'warning'}
          message={data?.ready
            ? (hasTaskIssue ? '表结构已就绪，但存在需要处理的同步任务' : '美团财务同步链路已就绪')
            : '正在检查或表结构尚未就绪'}
          description="本页仅检查数据库表、字段、索引和同步任务，不会请求美团接口或修改订单数据。"
          style={{ marginBottom: 20 }}
        />

        {data ? (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col xs={12} sm={8} lg={4}>
                <Statistic title="待同步" value={taskCounts.pending || 0} />
              </Col>
              <Col xs={12} sm={8} lg={4}>
                <Statistic title="同步中" value={taskCounts.processing || 0} />
              </Col>
              <Col xs={12} sm={8} lg={4}>
                <Statistic title="已完成" value={taskCounts.completed || 0} prefix={<CheckCircleOutlined />} />
              </Col>
              <Col xs={12} sm={8} lg={4}>
                <Statistic title="已失败" value={failedCount} valueStyle={failedCount ? { color: '#cf1322' } : undefined} />
              </Col>
              <Col xs={12} sm={8} lg={4}>
                <Statistic title="超时处理中" value={staleProcessingCount} valueStyle={staleProcessingCount ? { color: '#d46b08' } : undefined} />
              </Col>
              <Col xs={12} sm={8} lg={4}>
                <Descriptions size="small" column={1} colon={false}>
                  <Descriptions.Item label="检查时间">{formatDateTime(data.checkedAt)}</Descriptions.Item>
                  <Descriptions.Item label="最近完成">{formatDateTime(data.syncTasks.lastCompletedAt)}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            {data.syncTasks.lastError ? (
              <Alert
                type={hasTaskIssue ? 'error' : 'info'}
                showIcon
                message="最近同步信息"
                description={data.syncTasks.lastError}
                style={{ marginBottom: 20 }}
              />
            ) : null}

            {data.recommendations?.length ? (
              <Card size="small" title="处理建议" style={{ marginBottom: 20 }}>
                <List
                  size="small"
                  dataSource={data.recommendations}
                  renderItem={(item) => <List.Item>{item}</List.Item>}
                />
              </Card>
            ) : null}

            <Card size="small" title="表结构与索引检查">
              <Table
                rowKey="table"
                columns={schemaColumns}
                dataSource={data.schema}
                pagination={false}
                size="small"
                scroll={{ x: 900 }}
              />
            </Card>
          </>
        ) : !loading ? <Empty description="暂未获取到健康检查结果" /> : null}
      </Card>
    </div>
  );
};

export default MeituanFinancialHealth;
